import { useDispatch, useSelector } from 'react-redux'
import { ChevronDown, LogOut, RotateCw, User } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { AppDispatch, RootState } from '@/store/store'
import { useToast } from '@/hooks/useToast'
import { useLogoutRedirect } from '@/features/auth/api/logoutRedirect'
import { DropdownMenuGroup } from '@radix-ui/react-dropdown-menu'
import { useQueryClient } from '@tanstack/react-query'
import { useRefreshToken } from '@/features/auth/api/refreshToken'
import { returnTokenAction } from '@/store/actions/returnTokenAction'
import { locationActions } from '@/store/slices/locationSlice'

export const ProfileDropdown = () => {
  return (
    <DropdownMenu>
      <Opener />
      <Content />
    </DropdownMenu>
  )
}

const Opener = () => {
  const user = useSelector((state: RootState) => state.user)

  return (
    <DropdownMenuTrigger asChild>
      <div className="flex items-center space-x-2 cursor-pointer">
        <Avatar>
          <AvatarFallback>
            <User strokeWidth={1} />
          </AvatarFallback>
        </Avatar>
        <span>
          {user.originalUser ? (
            <>
              <span className="font-medium">{user.originalUser.firstName}</span> (behalf of{' '}
              <span className="font-medium">{user.firstName}</span>)
            </>
          ) : (
            <span className="font-medium">{user.firstName}</span>
          )}
          <ChevronDown className="h-4 w-4 inline" />
        </span>
      </div>
    </DropdownMenuTrigger>
  )
}

const Content = () => {
  const user = useSelector((state: RootState) => state.user)

  return (
    <DropdownMenuContent className="min-w-56 mx-4">
      <DropdownMenuLabel>
        <p className="font-medium">{user.name}</p>
        <p className="text-xs text-muted-foreground leading-none">{user.email}</p>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      {user.originalUser && (
        <>
          <DropdownMenuGroup>
            <ReturnUserDropdownItem />
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
        </>
      )}
      <LogoutDropdownItem />
    </DropdownMenuContent>
  )
}

const ReturnUserDropdownItem = () => {
  const user = useSelector((state: RootState) => state.user)
  const auth = useSelector((state: RootState) => state.auth)
  const dispatch = useDispatch<AppDispatch>()
  const queryClient = useQueryClient()
  const { isPending, mutateAsync } = useRefreshToken({
    mutationConfig: {
      onSuccess: async (res) => {
        dispatch(locationActions.clearLocation())
        await dispatch(returnTokenAction({ authResponse: res, queryClient })).unwrap()
      },
    },
  })

  if (!user.originalUser || !auth.originalAuth) {
    return
  }

  return (
    <DropdownMenuItem
      onClick={() => mutateAsync({ refreshToken: auth.originalAuth!.refreshToken })}
      disabled={isPending}
      className="text-success focus:text-success"
    >
      <RotateCw /> Return to {user.originalUser.firstName}
    </DropdownMenuItem>
  )
}

const LogoutDropdownItem = () => {
  const { idToken, originalAuth } = useSelector((state: RootState) => state.auth)
  const { toast } = useToast()

  const { mutate, isPending } = useLogoutRedirect({
    mutationConfig: {
      onSuccess: () => sessionStorage.clear(),
      onError: () => {
        toast({
          variant: 'destructive',
          title: 'Unexpected error',
          description: 'Error during redirection',
        })
      },
    },
  })
  return (
    <DropdownMenuItem onClick={() => mutate({ idToken: originalAuth?.idToken || idToken })} disabled={isPending}>
      <LogOut />
      Log out
    </DropdownMenuItem>
  )
}
